import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type TabItem<V extends string> = { value: V; label: ReactNode; badge?: ReactNode; disabled?: boolean; tone?: 'success' | 'danger' | 'accent' | 'grey' }

type TabsProps<V extends string> = {
  items: TabItem<V>[]
  value: V
  onChange: (value: V) => void
  label: string
  variant?: 'segmented' | 'underline'
  className?: string
}

/**
 * WAI-ARIA tablist with roving focus and arrow-key movement. The selection
 * indicator is one element that glides between tabs.
 */
/** Last indicator geometry per tab list, so a remounted list slides instead of jumping. */
const lastIndicator = new Map<string, CSSProperties>()

const TAB_TONE_TEXT: Record<string, string> = { success: 'text-success', danger: 'text-danger', accent: 'text-accent', grey: 'text-fg' }

export function Tabs<V extends string>({ items, value, onChange, label, variant = 'segmented', className }: TabsProps<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const listRef = useRef<HTMLDivElement>(null)
  const memoryKey = `${variant}:${label}:${items.map((item) => item.value).join('|')}`
  // A page that re-keys on the chosen tab remounts this list; start from where the pill last was.
  const [indicator, setIndicator] = useState<CSSProperties | null>(() => lastIndicator.get(memoryKey) ?? null)
  const [animated, setAnimated] = useState(false)
  const [settled, setSettled] = useState(() => !lastIndicator.has(memoryKey))
  const activeIndex = items.findIndex((item) => item.value === value)
  const activeTone = items[activeIndex]?.tone

  useLayoutEffect(() => {
    if (!settled) return
    const measure = () => {
      const node = refs.current[activeIndex]
      if (!node) return setIndicator(null)
      const next = { width: node.offsetWidth, transform: `translateX(${node.offsetLeft}px)` }
      lastIndicator.set(memoryKey, next)
      setIndicator(next)
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (listRef.current) observer.observe(listRef.current)
    return () => observer.disconnect()
  }, [activeIndex, items.length, memoryKey, settled])

  // Remounted: paint the remembered position once, then slide to the current tab.
  useLayoutEffect(() => {
    if (settled) return
    const frame = requestAnimationFrame(() => {
      setAnimated(true)
      setSettled(true)
    })
    return () => cancelAnimationFrame(frame)
  }, [settled])

  // Skip the slide on first paint so the pill doesn't fly in from the left.
  useLayoutEffect(() => {
    if (indicator && !animated) {
      const frame = requestAnimationFrame(() => setAnimated(true))
      return () => cancelAnimationFrame(frame)
    }
  }, [indicator, animated])

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const enabled = items.map((item, i) => (item.disabled ? -1 : i)).filter((i) => i >= 0)
    const position = enabled.indexOf(index)
    let next: number | undefined
    if (event.key === 'ArrowRight') next = enabled[(position + 1) % enabled.length]
    if (event.key === 'ArrowLeft') next = enabled[(position - 1 + enabled.length) % enabled.length]
    if (event.key === 'Home') next = enabled[0]
    if (event.key === 'End') next = enabled[enabled.length - 1]
    if (next === undefined) return
    event.preventDefault()
    refs.current[next]?.focus()
    onChange(items[next].value)
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={cn(
        'scrollbar-none relative flex max-w-full items-center overflow-x-auto',
        variant === 'segmented' ? 'w-fit gap-0.5 rounded-[calc(12px*var(--radius-scale))] bg-hover p-0.5' : 'gap-5 border-b border-line',
        className,
      )}
    >
      {indicator && (
        <span
          aria-hidden
          style={indicator}
          className={cn(
            'pointer-events-none absolute left-0',
            animated && 'transition-[transform,width] duration-[320ms] ease-[var(--ease-spring)]',
            variant === 'segmented'
              ? cn(
                  'top-0.5 bottom-0.5 rounded-[calc(10px*var(--radius-scale))] shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_var(--glass-highlight)]',
                  activeTone === 'success'
                    ? 'bg-success-soft'
                    : activeTone === 'danger'
                      ? 'bg-danger-soft'
                      : activeTone === 'accent'
                        ? 'bg-accent-soft'
                        : activeTone === 'grey'
                          ? 'bg-[color-mix(in_srgb,var(--muted)_18%,transparent)]'
                          : 'bg-glass-solid',
                )
              : '-bottom-px h-0.5 rounded-full bg-accent',
          )}
        />
      )}
      {items.map((item, index) => {
        const selected = item.value === value
        return (
          <button
            key={item.value}
            ref={(node) => {
              refs.current[index] = node
            }}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'relative z-[1] inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[length:calc(13px*var(--text-scale)*var(--ui-size))] font-medium transition-colors duration-[var(--dur-base)] disabled:opacity-40',
              variant === 'segmented' ? 'h-[calc(var(--spacing)*7.5*var(--ui-size))] rounded-[calc(10px*var(--radius-scale))] px-[calc(var(--spacing)*3*var(--ui-size))]' : '-mb-px h-[calc(var(--spacing)*9*var(--ui-size))]',
              selected ? (TAB_TONE_TEXT[item.tone ?? ''] ?? 'text-fg') : 'text-muted hover:text-fg',
              // Until measured, fall back to a static highlight.
              selected && !indicator && variant === 'segmented' && 'bg-glass-solid',
            )}
          >
            {item.label}
            {item.badge}
          </button>
        )
      })}
    </div>
  )
}
